import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getInsights } from "../controllers/insightsController";
import "./InsightsTab.css";

const CATEGORY_CLASS = {
  Voltage: "category category-blue",
  Current: "category category-violet",
  Power: "category category-orange",
  "Power Factor": "category",
  Energy: "category category-green",
  Frequency: "category category-pink",
  "THD Voltage": "category category-indigo",
  "THD Current": "category category-rose",
};

const OVER_TYPES = ["THD Voltage", "THD Current"];

function fmtPkr(value) {
  return Number(value || 0).toLocaleString("en-US");
}

function toIsoDate(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Default window: last 3 days ending today (data-relative, not hard-coded).
function defaultRange(days = 3) {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - days + 1);
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

function getUsername() {
  let user = null;
  try {
    const stored = localStorage.getItem("user");
    if (stored) user = JSON.parse(stored);
  } catch (e) {
    user = null;
  }
  return (
    localStorage.getItem("username") ||
    user?.username ||
    user?.userName ||
    user?.email ||
    user?.emailAddress ||
    ""
  );
}

/* ---- Icons ---- */
const Icon = ({ name, className, size = 24, color = "currentColor" }) => {
  const common = {
    width: size,
    height: size,
    fill: "none",
    viewBox: "0 0 24 24",
    stroke: color,
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className,
  };
  switch (name) {
    case "chart":
      return (<svg {...common}><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>);
    case "coin":
      return (<svg {...common}><path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>);
    case "check":
      return (<svg {...common}><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>);
    case "eye":
      return (<svg {...common}><path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>);
    case "close":
      return (<svg {...common}><path d="M6 18L18 6M6 6l12 12" /></svg>);
    case "clock":
      return (<svg {...common}><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>);
    case "tick":
      return (<svg {...common}><path d="M5 13l4 4L19 7" /></svg>);
    case "info":
    default:
      return (<svg {...common}><path d="M12 16v-4m0-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>);
  }
};

/* Lightning-bolt loader — mirrors the Historical Overview global loader. */
function LightningLoader() {
  return (
    <div className="insights-loader-overlay" role="status" aria-live="polite">
      <div className="insights-loader-content">
        <div className="insights-lightning-loader" aria-hidden="true">
          <svg viewBox="0 0 160 247" focusable="false">
            <defs>
              <linearGradient id="insProgress" x1="80" y1="241.5" x2="80" y2="5.5" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#009F8D" stopOpacity="1" />
                <stop offset="0%" stopColor="#009F8D" stopOpacity="1">
                  <animate attributeName="offset" values="0;0.76;1;1;0" keyTimes="0;0.5;0.72;0.86;1"
                    dur="2.2s" repeatCount="indefinite" calcMode="spline"
                    keySplines="0.4 0 0.2 1;0.4 0 0.2 1;0.4 0 0.2 1;0.4 0 0.2 1" />
                </stop>
                <stop offset="0%" stopColor="#009F8D" stopOpacity="0">
                  <animate attributeName="offset" values="0;0.76;1;1;0" keyTimes="0;0.5;0.72;0.86;1"
                    dur="2.2s" repeatCount="indefinite" calcMode="spline"
                    keySplines="0.4 0 0.2 1;0.4 0 0.2 1;0.4 0 0.2 1;0.4 0 0.2 1" />
                </stop>
                <stop offset="100%" stopColor="#009F8D" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path className="insights-bolt-shape" fill="url(#insProgress)" stroke="#009F8D"
              strokeWidth="10" strokeLinejoin="round"
              d="M137.641 5.5H60.1789L5.5 135.194H67.9251L32.8394 241.5L154.5 86.718H98.9098L137.641 5.5Z" />
          </svg>
        </div>
      </div>
    </div>
  );
}

function SeverityPill({ severity }) {
  return (
    <span className={`pill ${severity === "Critical" ? "critical" : "warning"}`}>
      <span className="tiny-dot" />
      {severity}
    </span>
  );
}

function CategoryPill({ category }) {
  return <span className={`pill ${CATEGORY_CLASS[category] || "category"}`}>{category}</span>;
}

/* ---- Detail chart (SVG, ported from mockup) ---- */
function InsightChart({ row }) {
  const points = row.chartData || [];
  if (!points.length) return null;

  const width = 760;
  const height = 220;
  const padX = 36;
  const padY = 22;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const values = points.map((p) => p.value);
  const threshold = points[0]?.threshold ?? 0;
  const maxVal = Math.max(...values, threshold);
  const minVal = Math.min(...values, threshold);
  const span = Math.max(maxVal - minVal, 1);
  const xStep = points.length > 1 ? innerW / (points.length - 1) : 0;
  const y = (v) => padY + ((maxVal - v) / span) * innerH;
  const x = (i) => padX + i * xStep;

  const linePoints = points.map((p, i) => `${x(i)},${y(p.value)}`).join(" ");
  const areaPoints = `${padX},${height - padY} ${linePoints} ${width - padX},${height - padY}`;

  const yTicks = 4;
  const gridLines = [];
  for (let i = 0; i <= yTicks; i++) {
    const val = maxVal - (span * i) / yTicks;
    const yPos = y(val);
    gridLines.push(
      <line key={`g-${i}`} x1={padX} y1={yPos.toFixed(1)} x2={width - padX} y2={yPos.toFixed(1)}
        stroke="rgba(148,163,184,.22)" strokeDasharray="4 4" />
    );
    gridLines.push(
      <text key={`gt-${i}`} x={10} y={(yPos + 4).toFixed(1)} fill="#94a3b8" fontSize="10">
        {val.toFixed(row.chartUnit === "PF" ? 2 : 1)} {row.chartUnit}
      </text>
    );
  }

  // shaded breach segments
  const segments = [];
  let startIdx = null;
  points.forEach((point, index) => {
    const breached = OVER_TYPES.includes(row.category)
      ? point.value > threshold
      : point.value < threshold;
    if (breached && startIdx === null) startIdx = index;
    if (!breached && startIdx !== null) {
      segments.push([startIdx, index - 1]);
      startIdx = null;
    }
  });
  if (startIdx !== null) segments.push([startIdx, points.length - 1]);

  const thresholdY = y(threshold);
  const thresholdLabelX = width - padX - 84;
  const isCritical = row.severity === "Critical";

  return (
    <svg className="chart-box" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Chart for ${row.name}`}>
      <rect x="0" y="0" width={width} height={height} rx="16" fill="#f8fafc" />
      {gridLines}
      {segments.map(([s, e], i) => {
        const x1 = x(s);
        const x2 = x(e);
        return (
          <rect key={`seg-${i}`} x={x1} y={padY} width={Math.max(x2 - x1, 0)} height={innerH}
            fill="rgba(27,160,142,.08)" rx="10" />
        );
      })}
      <line x1={padX} y1={thresholdY.toFixed(1)} x2={width - padX} y2={thresholdY.toFixed(1)}
        stroke={isCritical ? "#ef4444" : "#f59e0b"} strokeDasharray="6 4" strokeWidth="1.5" />
      <rect x={thresholdLabelX} y={Math.max(thresholdY - 14, 10)} width="78" height="20" rx="10"
        fill="white" stroke={isCritical ? "#fecaca" : "#fde68a"} />
      <text x={thresholdLabelX + 39} y={Math.max(thresholdY + 1, 24)} textAnchor="middle"
        fill={isCritical ? "#dc2626" : "#d97706"} fontSize="9" fontWeight="700">Threshold</text>
      <polygon points={areaPoints} fill="rgba(27,160,142,.10)" opacity="0.9" />
      <polyline points={linePoints} fill="none" stroke="#1ba08e" strokeWidth="2.4"
        strokeLinejoin="round" strokeLinecap="round" />
      {points.map((point, index) => (
        <circle key={`c-${index}`} cx={x(index)} cy={y(point.value)} r="3.5" fill="#1ba08e" stroke="#fff" strokeWidth="2" />
      ))}
      {points.map((point, index) => (
        <text key={`t-${index}`} x={x(index)} y={height - 6} textAnchor="middle" fill="#94a3b8" fontSize="10">
          {point.time}
        </text>
      ))}
    </svg>
  );
}

/* ---- Detail modal ---- */
function DetailModal({ row, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!row) return null;
  const warningBreached = row.breachedThreshold === "warning";
  const criticalBreached = row.breachedThreshold === "critical";

  return (
    <div className="insights-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="detail-backdrop" onMouseDown={onClose} />
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="ins-modal-title">
        <div className="modal-head">
          <div className="modal-head-row">
            <div>
              <div className="modal-title-row">
                <h2 id="ins-modal-title">{row.name}</h2>
                <CategoryPill category={row.category} />
                <SeverityPill severity={row.severity} />
              </div>
              <div className="modal-machine">
                Machine: <strong style={{ color: "#0f172a" }}>{row.machine}</strong>
              </div>
            </div>
            <button className="close-btn" onClick={onClose} aria-label="Close modal">
              <Icon name="close" size={18} />
            </button>
          </div>
        </div>

        <div className="modal-body">
          <section className="section">
            <div className="section-title">Detected Occurrences</div>
            <ul className="occ-list">
              {(row.occurrences || []).map((item, i) => (
                <li className="occ-item" key={i}>
                  <Icon name="clock" className="occ-icon" size={14} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="section">
            <div className="section-title">Description</div>
            <p style={{ margin: 0, color: "#334155", lineHeight: 1.65, fontSize: 14 }}>{row.description}</p>
          </section>

          <section className="section">
            <div className="section-title">Threshold Reference</div>
            <div className="threshold-grid">
              <div className={`threshold-card ${warningBreached ? "breached-warning" : ""}`}>
                <div className="threshold-row">
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", display: "inline-block" }} />
                  <strong style={{ color: "#b45309", fontSize: 12 }}>Warning</strong>
                  {warningBreached && <span className="badge warn">Breached</span>}
                </div>
                <div style={{ color: "#475569", fontSize: 13, lineHeight: 1.5 }}>{row.warningThreshold}</div>
              </div>
              <div className={`threshold-card ${criticalBreached ? "breached-critical" : ""}`}>
                <div className="threshold-row">
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444", display: "inline-block" }} />
                  <strong style={{ color: "#b91c1c", fontSize: 12 }}>Critical</strong>
                  {criticalBreached && <span className="badge crit">Breached</span>}
                </div>
                <div style={{ color: "#475569", fontSize: 13, lineHeight: 1.5 }}>{row.criticalThreshold}</div>
              </div>
            </div>
          </section>

          <section className="section">
            <div className="section-title">{row.chartParam} - Detected Period</div>
            <div className="chart-shell">
              <InsightChart row={row} />
            </div>
          </section>

          <div className="detail-grid-2">
            <section className="section">
              <div className="section-title">Reasons / Causes</div>
              <ul className="bullet-list">
                {(row.reasons || []).map((item, i) => (
                  <li className="bullet-item" key={i}>
                    <span className="reason-bullet" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
            <section className="section">
              <div className="section-title">Fix / Remedy</div>
              <ul className="bullet-list">
                {(row.fixes || []).map((item, i) => (
                  <li className="bullet-item" key={i}>
                    <Icon name="tick" className="tick-icon" size={14} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <div className="impact-grid">
            <div className="impact-item">
              <div className="impact-label">Cost Impact</div>
              <p className="impact-value">PKR {fmtPkr(row.cost)}</p>
            </div>
            <div className="impact-item">
              <div className="impact-label">Energy Loss</div>
              <p className="impact-value">{row.loss} kWh</p>
            </div>
            <div className="impact-item">
              <div className="impact-label">Savings Potential</div>
              <p className="impact-value green">PKR {fmtPkr(row.savings)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---- Page ---- */
export default function InsightsTab() {
  const username = useMemo(getUsername, []);
  const initialRange = useMemo(() => defaultRange(3), []);

  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);
  const [machine, setMachine] = useState("All");
  const [severity, setSeverity] = useState("All");
  const [category, setCategory] = useState("All");

  const [insights, setInsights] = useState([]);
  const [categories, setCategories] = useState([]);
  const [machines, setMachines] = useState([]);
  const [summary, setSummary] = useState({
    totalLoss: 0, totalCost: 0, totalSavings: 0, criticalCount: 0, warningCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalId, setModalId] = useState(null);

  const load = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getInsights({
        username,
        machine: overrides.machine ?? machine,
        severity: overrides.severity ?? severity,
        category: overrides.category ?? category,
        from: overrides.from ?? fromDate,
        to: overrides.to ?? toDate,
      });
      setInsights(data.insights);
      setSummary(data.summary);
      if (data.categories.length) setCategories(data.categories);
      if (data.machines.length) setMachines(data.machines);
    } catch (e) {
      setError("Unable to load insights. Please try again.");
      setInsights([]);
    } finally {
      setLoading(false);
    }
  }, [username, machine, severity, category, fromDate, toDate]);

  // Initial load only; subsequent loads happen on Apply / quick-range / clear.
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const applyQuickRange = (days) => {
    const { from: fromIso, to: toIso } = defaultRange(days);
    setFromDate(fromIso);
    setToDate(toIso);
    load({ from: fromIso, to: toIso });
  };

  const clearFilters = () => {
    setMachine("All");
    setSeverity("All");
    setCategory("All");
    load({ machine: "All", severity: "All", category: "All" });
  };

  const activeFilter = machine !== "All" || severity !== "All" || category !== "All";
  const modalRow = insights.find((r) => r.id === modalId) || null;

  return (
    <div className="insights-page">
      <div className="page">
        <div className="page-head">
          <div>
            <h1>Insights</h1>
            <div className="subtle">Power quality &amp; energy anomalies</div>
          </div>
          <div className="status-row">
            <span className="status-pill" style={{ color: "#dc2626" }}>
              <span className="dot" />{summary.criticalCount} Critical
            </span>
            <span className="status-pill" style={{ color: "#d97706" }}>
              <span className="dot" />{summary.warningCount} Warning
            </span>
          </div>
        </div>

        <section className="panel filters">
          <div className="filter-grid">
            <div className="field">
              <label>From</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="field">
              <label>To</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
            <div className="quick-ranges">
              <button className="quick-btn" onClick={() => applyQuickRange(3)}>3D</button>
              <button className="quick-btn" onClick={() => applyQuickRange(7)}>7D</button>
              <button className="quick-btn" onClick={() => applyQuickRange(30)}>30D</button>
            </div>
            <div className="field">
              <label>Machine</label>
              <select value={machine} onChange={(e) => setMachine(e.target.value)}>
                <option value="All">All Machines</option>
                {machines.map((name) => (<option key={name} value={name}>{name}</option>))}
              </select>
            </div>
            <div className="field">
              <label>Severity</label>
              <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
                <option value="All">All Severities</option>
                <option value="Critical">Critical</option>
                <option value="Warning">Warning</option>
              </select>
            </div>
            <div className="field">
              <label>Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="All">All Categories</option>
                {categories.map((name) => (<option key={name} value={name}>{name}</option>))}
              </select>
            </div>
            <button className="primary-btn" onClick={() => load()}>Apply</button>
            {activeFilter && <button className="ghost-btn" onClick={clearFilters}>Clear</button>}
          </div>
        </section>

        <section className="summary-grid">
          <div className="stat-card" style={{ background: "#002f46" }}>
            <Icon name="chart" className="stat-icon" size={36} />
            <div>
              <p className="stat-label">Total Energy Loss</p>
              <div className="stat-value">{Number(summary.totalLoss).toFixed(1)} kWh</div>
              <div className="stat-sub">In selected period</div>
            </div>
          </div>
          <div className="stat-card" style={{ background: "#ff9c07" }}>
            <Icon name="coin" className="stat-icon" size={36} />
            <div>
              <p className="stat-label">Total Cost Impact</p>
              <div className="stat-value">PKR {fmtPkr(summary.totalCost)}</div>
              <div className="stat-sub">Losses + penalties incurred</div>
            </div>
          </div>
          <div className="stat-card" style={{ background: "#009f8d" }}>
            <Icon name="check" className="stat-icon" size={36} />
            <div>
              <p className="stat-label">Total Savings Potential</p>
              <div className="stat-value">PKR {fmtPkr(summary.totalSavings)}</div>
              <div className="stat-sub">If all fixes are implemented</div>
            </div>
          </div>
        </section>

        {loading ? (
          <div className="panel" style={{ minHeight: 320 }} />
        ) : error ? (
          <div className="panel empty-state">
            <div>
              <div className="empty-icon"><Icon name="info" size={28} /></div>
              <p className="empty-title">{error}</p>
              <p className="empty-desc">Check your connection and press Apply to retry</p>
            </div>
          </div>
        ) : insights.length === 0 ? (
          <div className="panel empty-state">
            <div>
              <div className="empty-icon"><Icon name="info" size={28} /></div>
              <p className="empty-title">No insights detected in this period</p>
              <p className="empty-desc">Try adjusting the date range or clearing filters</p>
            </div>
          </div>
        ) : (
          <div className="panel table-wrap">
            <div className="table-head">
              <p className="table-title">{insights.length} insight{insights.length !== 1 ? "s" : ""} detected</p>
              <p className="table-sub">Sorted: Critical first</p>
            </div>
            <div className="scroll-x">
              <table>
                <thead>
                  <tr>
                    <th>Insight</th>
                    <th>Machine</th>
                    <th>Category</th>
                    <th>Severity</th>
                    <th>Detected</th>
                    <th style={{ textAlign: "right" }}>Cost (PKR)</th>
                    <th style={{ textAlign: "right" }}>Loss (kWh)</th>
                    <th style={{ textAlign: "right" }}>Savings (PKR)</th>
                    <th style={{ textAlign: "center" }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {insights.map((row) => (
                    <tr key={row.id}>
                      <td style={{ boxShadow: `inset 3px 0 0 ${row.severity === "Critical" ? "#ef4444" : "#f59e0b"}` }}>
                        {row.name}
                      </td>
                      <td>{row.machine}</td>
                      <td><CategoryPill category={row.category} /></td>
                      <td><SeverityPill severity={row.severity} /></td>
                      <td style={{ color: "#64748b", fontSize: 12 }}>{row.detected}</td>
                      <td className="numeric">
                        {row.cost > 0 ? fmtPkr(row.cost) : <span className="muted-cell">—</span>}
                      </td>
                      <td className="numeric">
                        {row.loss > 0 ? row.loss.toFixed(1) : <span className="muted-cell">—</span>}
                      </td>
                      <td className="numeric" style={{ fontWeight: 800, color: "#1ba08e" }}>
                        {row.savings > 0 ? fmtPkr(row.savings) : <span className="muted-cell">—</span>}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button className="action-btn" onClick={() => setModalId(row.id)}>
                          <Icon name="eye" size={14} />
                          <span style={{ marginLeft: 6 }}>View</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="totals">
                    <td colSpan={5} style={{ fontSize: 12, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      Totals ({insights.length} rows)
                    </td>
                    <td className="numeric" style={{ fontWeight: 900, color: "#334155" }}>{fmtPkr(summary.totalCost)}</td>
                    <td className="numeric" style={{ fontWeight: 900, color: "#334155" }}>{Number(summary.totalLoss).toFixed(1)}</td>
                    <td className="numeric" style={{ fontWeight: 900, color: "#1ba08e" }}>{fmtPkr(summary.totalSavings)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>

      {modalRow && <DetailModal row={modalRow} onClose={() => setModalId(null)} />}
      {loading && <LightningLoader />}
    </div>
  );
}
